'use client';

import { ChevronIcon, CloseIcon } from '@insta-club/components';
import { cn } from '@insta-club/utilities';
import { cva, type VariantProps } from 'class-variance-authority';
import clsx from 'clsx';
import Image from 'next/image';
import { FC, useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import LoadingIcon from 'src/libs/components/icons/loading';

interface Option {
    value: string | number;
    label: string;
    image?: string;
    description?: string;
}

const containerVariants = cva(
    'relative w-full rounded-lg border bg-white px-3 transition-all duration-200 text-sm font-medium',
    {
        variants: {
            variant: {
                default: 'border-gray-300 focus-within:border-primary-600',
                error: 'border-danger-600 focus-within:border-danger-600',
                active: 'border-primary-600 focus-within:border-primary-600'
            },
            size: {
                default: 'h-14',
                sm: 'h-12',
                lg: 'h-16'
            }
        },
        defaultVariants: {
            variant: 'default',
            size: 'default'
        }
    }
);

const floatingLabelVariants = cva(
    'absolute right-3 text-gray-400 transition-all duration-200 pointer-events-none bg-white px-1',
    {
        variants: {
            variant: {
                default: 'peer-focus:text-primary-600',
                error: 'text-danger-600 peer-focus:text-danger-600',
                active: 'text-primary-600 peer-focus:text-primary-600'
            },
            size: {
                default:
                    'top-4 peer-focus:-top-2 peer-focus:text-xs peer-[:not(:placeholder-shown)]:-top-2 peer-[:not(:placeholder-shown)]:text-xs',
                sm: 'top-3 peer-focus:-top-2 peer-focus:text-xs peer-[:not(:placeholder-shown)]:-top-2 peer-[:not(:placeholder-shown)]:text-xs',
                lg: 'top-5 peer-focus:-top-2 peer-focus:text-xs peer-[:not(:placeholder-shown)]:-top-2 peer-[:not(:placeholder-shown)]:text-xs'
            }
        },
        defaultVariants: {
            variant: 'default',
            size: 'default'
        }
    }
);

const badgeVariants = cva(
    'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-all duration-200 cursor-pointer hover:shadow-sm',
    {
        variants: {
            selected: {
                true: 'bg-primary-600 text-white border-primary-600',
                false: 'text-primary-700 border-primary-500 hover:bg-primary-100'
            }
        },
        defaultVariants: {
            selected: false
        }
    }
);

export interface BadgeSelectSingleProps extends VariantProps<typeof containerVariants> {
    name: string;
    label?: string;
    options: Option[];
    placeholder?: string;
    disabled?: boolean;
    isSearchable?: boolean;
    isLoading?: boolean;
    onChange?: (item: Option | null) => void;
    startAdornment?: React.ReactElement;
    endAdornment?: React.ReactElement;
}

const BadgeSelectSingle: FC<BadgeSelectSingleProps> = ({
    name,
    label,
    options,
    placeholder = 'انتخاب کنید',
    disabled,
    isSearchable,
    variant,
    size,
    isLoading,
    onChange,
    startAdornment,
    endAdornment
}) => {
    const [showMenu, setShowMenu] = useState(false);
    const [selectedValue, setSelectedValue] = useState<Option | null>(null);
    const [searchValue, setSearchValue] = useState('');
    const inputRef = useRef<HTMLDivElement>(null);
    const searchRef = useRef<HTMLInputElement>(null);
    const { register, setValue, watch, formState } = useFormContext();

    const fieldValue = watch(name);
    const hasError = formState.errors[name];
    const currentVariant = hasError ? 'error' : variant;

    useEffect(() => {
        const handler = (event: MouseEvent) => {
            if (inputRef.current && !inputRef.current.contains(event.target as Node)) {
                setShowMenu(false);
            }
        };
        window.addEventListener('click', handler);
        return () => window.removeEventListener('click', handler);
    }, []);

    useEffect(() => {
        if (fieldValue) {
            const found = options.find((opt) => opt.value.toString() === fieldValue.toString());
            setSelectedValue(found || null);
        } else {
            setSelectedValue(null);
        }
    }, [fieldValue, options]);

    const handleSelect = (option: Option) => {
        setValue(name, option.value);
        setSelectedValue(option);
        setShowMenu(false);
        setSearchValue('');
        onChange?.(option);
    };

    const getFilteredOptions = () => {
        if (!searchValue) return options;
        return options.filter((o) => o.label.toLowerCase().includes(searchValue.toLowerCase()));
    };

    return (
        <div className='flex w-full flex-col gap-1'>
            <div
                ref={inputRef}
                className={cn(
                    containerVariants({ variant: showMenu ? 'active' : currentVariant, size }),
                    disabled && 'cursor-not-allowed bg-gray-100 opacity-60'
                )}
            >
                <div
                    className='flex h-full w-full cursor-pointer items-center gap-2'
                    onClick={() => !disabled && !isLoading && setShowMenu((p) => !p)}
                >
                    {startAdornment && <div className='flex items-center'>{startAdornment}</div>}

                    <div className='peer flex-1 text-base placeholder-transparent'>
                        {selectedValue ? (
                            <div className='flex items-center gap-2 text-sm'>
                                {selectedValue.image && (
                                    <Image
                                        src={selectedValue.image}
                                        alt={selectedValue.label}
                                        width={18}
                                        height={18}
                                        className='rounded-sm'
                                    />
                                )}
                                <span>{selectedValue.label}</span>
                            </div>
                        ) : (
                            <span className={'text-gray-400'}>{placeholder}</span>
                        )}
                    </div>

                    {endAdornment && <div className='flex items-center'>{endAdornment}</div>}

                    {isLoading && (
                        <div className='flex h-14 items-center justify-center py-4'>
                            <LoadingIcon className='text-secondary-500 h-5 w-5 animate-spin' />
                        </div>
                    )}

                    <ChevronIcon width={16} className={clsx('transition-transform', showMenu && '-rotate-90')} />

                    <label
                        className={cn(
                            floatingLabelVariants({ variant: showMenu ? 'active' : currentVariant, size }),
                            disabled && 'opacity-70'
                        )}
                    >
                        {label}
                    </label>
                </div>

                {showMenu && (
                    <div className='shadow-main bg-primary-50 absolute right-0 z-50 w-full translate-y-0.5 rounded-md border border-gray-200'>
                        {isSearchable && (
                            <div className='relative p-2.5'>
                                <input
                                    ref={searchRef}
                                    type='text'
                                    value={searchValue}
                                    onChange={(e) => setSearchValue(e.target.value)}
                                    placeholder='جستجو...'
                                    className='focus:border-primary-600 focus:ring-primary-100 w-full rounded-md border border-gray-200 bg-white px-3 py-2 pl-8 text-sm outline-none focus:ring-2'
                                />

                                {searchValue && (
                                    <button
                                        type='button'
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setSearchValue('');
                                            searchRef.current?.focus();
                                        }}
                                        className='absolute top-1/2 left-4 -translate-y-1/2 text-gray-400 hover:text-gray-600'
                                    >
                                        <CloseIcon width={10} />
                                    </button>
                                )}
                            </div>
                        )}
                        <div
                            className={cn('custom-scrollbar flex max-h-52 flex-wrap gap-1.5 overflow-auto p-2.5', {
                                'pt-0': isSearchable
                            })}
                        >
                            {getFilteredOptions().map((option) => {
                                const selected = option.value === selectedValue?.value;

                                return (
                                    <div
                                        key={option.value}
                                        onClick={() => handleSelect(option)}
                                        className={cn(badgeVariants({ selected }))}
                                    >
                                        {option.image && (
                                            <Image
                                                src={option.image}
                                                alt={option.label}
                                                width={16}
                                                height={16}
                                                className='rounded-sm'
                                            />
                                        )}
                                        <span>{option.label}</span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>

            {hasError && (
                <p className='text-danger-600 text-right text-sm font-medium'>
                    {formState.errors[name]?.message as string}
                </p>
            )}

            <input type='hidden' {...register(name)} />
        </div>
    );
};

export default BadgeSelectSingle;

